<script setup lang="ts">
definePageMeta({
  layout: 'blank',
});

const { t } = useTexts();
const { modalType } = storeToRefs(useLayoutStore());
const { openToaster } = useLayoutStore();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { userEmail, userId, fetchUserProfile } = useUserData();
const { isOnBetBrWithoutMigrateAccepted } = useUserValidations();
const { walletRealFormatted } = useWallet();
const { updateUserContract } = useUserDataStore();
const { openSupportChat, hasLiveSupport } = useLiveSupport();

const isLoading = ref(false);
const allContractsAccepted = ref(false);

async function updateContractStatus() {
  isLoading.value = true;

  if (!allContractsAccepted.value) {
    await sleep(100);

    allContractsAccepted.value = true;

    await sleep(500);
  }

  await updateUserContract('all')
    .then(async () => {
      await fetchUserProfile({ force: true });

      const message = t('account.migration.confirm.update_success');

      openToaster({
        message,
        type: 'success',
      });

      await navigateTo(localeLink('/'));
    })
    .catch((err) => {
      const errorData = err?.data?.message;

      const errorMessage =
        errorData || t('account.contracts_section.update_error');

      openToaster({
        message: errorMessage,
        type: 'error',
      });

      isLoading.value = false;
    });
}

watch(
  isOnBetBrWithoutMigrateAccepted,
  (value) => {
    if (!isAuthenticated.value || !value) {
      navigateTo(localeLink('/'));
    }
  },
  { immediate: true },
);

onMounted(() => {
  modalType.value = 'closed';
});
</script>

<template>
  <AuthPageLayout v-if="isAuthenticated">
    <BaseLoading
      class="!bg-bg-primary !absolute !z-[99]"
      :new-cactus-loader="true"
      :show="isLoading"
    />

    <div :class="$style.migration">
      <div :class="$style.title">
        <span>
          {{
            $t('account.migration.confirm.title', {
              appUrl: appUrl(),
            })
          }}
        </span>
      </div>

      <div
        :class="$style.description"
        v-html="
          $t('account.migration.confirm.description', {
            appUrl: appUrl(),
          })
        "
      />

      <div
        :class="[$style.description, $style.withdraw]"
        v-html="$t('account.migration.confirm.description_withdraw')"
      />

      <div :class="$style.buttons">
        <div :class="$style.userDetails">
          <span>
            {{ userEmail }}
          </span>
          <span> #{{ userId }} </span>
        </div>

        <div :class="[$style.userDetails, $style.balance]">
          <span>
            {{ $t('account.migration.confirm.balance') }}
          </span>
          <span>
            {{ walletRealFormatted }}
          </span>
        </div>

        <div :class="$style.contractField">
          <span>
            <BaseFormToggle
              v-model="allContractsAccepted"
              :class="$style.switch"
              :disabled="isLoading"
            />
          </span>
          <div>
            {{ $t('account.contracts_modal.accept_contracts') }}
            <NuxtLink
              target="_blank"
              to="/page/terms"
            >
              {{ $t('account.contracts_modal.terms_and_conditions') }}
            </NuxtLink>
            {{ $t('account.contracts_modal.and') }}
            <NuxtLink
              target="_blank"
              to="/page/privacy"
            >
              {{ $t('account.contracts_modal.privacy_policy') }}
            </NuxtLink>
            {{ $t('account.contracts_modal.and_the') }}
            <NuxtLink
              target="_blank"
              to="/page/consent"
            >
              {{ $t('account.contracts_modal.consent_term') }}
            </NuxtLink>
            {{ $t('account.contracts_modal.migrate_account') }}
          </div>
        </div>

        <button
          :class="$style.migrate"
          :disabled="isLoading"
          @click="updateContractStatus()"
        >
          <NuxtIcon
            v-if="!isLoading"
            name="migration"
          />
          <NuxtIcon
            v-else
            :class="[$style.loadingIcon, 'rotate-animation']"
            filled
            name="loading"
          />
          <span v-if="!isLoading">
            {{ $t('account.migration.confirm.migrate_now') }}
          </span>
          <span v-else>
            {{ $t('account.migration.modal.migrate_in_progress') }}
          </span>
        </button>

        <button
          v-if="hasLiveSupport"
          :class="$style.support"
          :disabled="isLoading"
          @click="openSupportChat()"
        >
          {{ $t('account.migration.confirm.support') }}
        </button>
      </div>
    </div>
  </AuthPageLayout>
</template>

<style module lang="postcss">
.migration {
  @apply flex flex-col items-center justify-center gap-4 text-auth-texts p-5;

  .title {
    @apply flex items-center text-white text-lg font-bold px-2 mt-1;
  }

  .description {
    @apply text-center text-sm font-medium px-3;

    span {
      @apply ml-1;
    }

    strong {
      @apply text-primary;
    }

    &.withdraw {
      @apply text-xs text-auth-texts/60;
    }
  }

  .buttons {
    @apply flex flex-col items-center justify-center gap-2 w-full px-4;

    .userDetails {
      @apply flex-1 flex items-center justify-between gap-2 w-full px-3 py-2 bg-texts/5 text-texts/50 text-xs rounded-lg mb-2;

      span {
        @apply font-semibold;

        &:last-of-type {
          @apply text-texts/30 font-medium;
        }
      }

      &.balance {
        @apply -mt-2;

        span {
          @apply font-medium;

          &:last-of-type {
            @apply text-primary/60 font-bold;
          }
        }
      }
    }

    .contractField {
      @apply w-full flex items-center justify-center gap-3 mt-1 mb-3;

      div {
        @apply text-xs leading-4;

        a {
          @apply text-primary border-b border-primary border-dotted;
        }

        strong {
          @apply font-semibold;
        }
      }
    }

    .migrate {
      @apply flex items-center justify-center gap-2 text-sm px-5 py-3 rounded-lg flex-1 w-full select-none bg-auth-button-bg text-auth-button-text font-bold;

      &:disabled {
        @apply opacity-40 cursor-not-allowed;
      }

      .loadingIcon {
        @apply text-sm;
      }
    }

    .support {
      @apply flex items-center justify-center gap-2 text-xs px-5 py-3 rounded-lg flex-1 w-full select-none bg-auth-texts/10 text-auth-texts/60 font-semibold;

      &:disabled {
        @apply opacity-40 cursor-not-allowed;
      }

      .loadingIcon {
        @apply text-sm;
      }
    }
  }
}
</style>
